// The rules that decide what the Release Notes tab has on screen.
//
// Ported from the service tool (MW_service_tool, apps/web/src/app/release-notes-view.ts), which is
// where MW first saw this page and liked it. Kept in its own module for the same reason it is kept
// in its own module there: these three questions (how often a query appears in a note, which notes
// a query leaves standing, and which single note is open once it has) are testable without a DOM,
// and the component below is then only markup plus two pieces of state.

// Escape a reader-typed string so it can sit inside a RegExp literally.
//
// The search box takes whatever is typed, and a release note legitimately contains "(", "+", "?"
// and "*" (work codes, keyboard hints, markdown bullets). Without this, typing a single "(" throws
// SyntaxError: Invalid regular expression and takes the whole tab down with it.
function escapeForRegExp(input) {
    return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// The query as the rest of this module uses it: trimmed, and empty when there is nothing to search for.
export function normaliseQuery(raw) {
    return (raw || '').trim();
}

// How many times the query appears in one note's markdown source, case insensitively.
//
// Counted against the SOURCE rather than the rendered text, which is deliberate and has one visible
// consequence: a hit that falls inside markdown syntax (the "**" of a bold run, say) is counted but
// cannot be highlighted, because the highlighter only ever sees rendered text. Rendering every note
// to a string just to count costs far more than a discrepancy that needs an asterisk or a backtick
// in the query to show up at all.
export function countMatches(body, query) {
    const q = normaliseQuery(query);
    if (!q) return 0;
    return ((body || '').match(new RegExp(escapeForRegExp(q), 'gi')) || []).length;
}

// The notes to offer, newest first, in the order the manifest already holds them.
//
// With no query every note is offered, so the list is the full history. With a query only the notes
// that contain it are, which is what turns the version list into a result list without the tab
// needing a second layout for searching.
export function scoreNotes(notes, query) {
    const q = normaliseQuery(query);
    if (!q) return (notes || []).map(note => ({ note, matches: 0 }));
    return (notes || [])
        .map(note => ({ note, matches: countMatches(note.body, q) }))
        .filter(scored => scored.matches > 0);
}

// Which version is open, given what the reader last picked and what they have since typed.
//
// Three rules, in order:
//
//  1. Nothing left standing means nothing is open, and the tab says so instead of rendering a stale
//     note underneath a search that excluded it.
//  2. A requested version that survived the search stays open. Typing a query that still matches the
//     note you are reading must not move you somewhere else mid sentence.
//  3. Otherwise the first survivor opens, which is the newest that matched, because the manifest is
//     newest first. That is also what makes the newest release the default: on first render nothing
//     is requested yet.
export function resolveSelectedVersion(scored, requested) {
    const first = (scored || [])[0];
    if (first === undefined) return null;
    if (requested !== null && requested !== undefined && scored.some(s => s.note.version === requested)) return requested;
    return first.note.version;
}

// Split rendered text into alternating plain and matching runs, so the component can wrap the
// matching ones in <mark>.
//
// Returning data rather than markup is the point: the notes are rendered through React elements and
// never through dangerouslySetInnerHTML, so a highlighter that returned a string of markup would
// reintroduce exactly the injection risk the renderer avoids. The notes are ours, but the query is
// whatever the reader typed.
export function splitOnQuery(text, query) {
    const q = normaliseQuery(query);
    if (!q || !text) return [{ text, hit: false }];
    return text
        .split(new RegExp(`(${escapeForRegExp(q)})`, 'gi'))
        .filter(part => part !== '')
        .map(part => ({ text: part, hit: part.toLowerCase() === q.toLowerCase() }));
}

// A release date as a reader would write it ("9. september 2026").
//
// Built and formatted in UTC on purpose. A manifest date is a plain YYYY-MM-DD, which new Date()
// parses as UTC midnight, so formatting it in the viewer's local zone renders the day before for
// anyone west of Greenwich. MW are in Copenhagen, but the page should not be quietly wrong for a
// colleague reading it from a laptop abroad.
export function formatReleaseDate(iso, month = 'long') {
    const [y, m, d] = String(iso || '').split('-').map(Number);
    if (!y || !m || !d) return iso;
    return new Intl.DateTimeFormat('da-DK', {
        day: 'numeric',
        month,
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(Date.UTC(y, m - 1, d)));
}
